// Receipt detail > Digital Waste Tracking panel (admin only, only while DWT is on).
//
// Shows where the receipt stands with Defra, the deadline, every attempt (in our
// own words, never Defra's), what will be / was reported, and lets an admin edit
// the DWT details (weight, carrier vehicle, consignment) and send an amendment.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileUp, Save } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  fetchReceiptDwtState,
  recordErrorNotified,
  saveReceiptExtras,
  sendReceiptAmendment,
  submitReceiptNow,
  type DwtDueState,
  type DwtExtrasPatch,
  type DwtMessage,
  type DwtReceiptState,
  type DwtSubmissionDto,
  type DwtSummary,
  type NoConsignmentReasonCode,
} from '../../../api/dwt';
import { useToast } from '../../../hooks/useToast';
import { DwtDueBadge, DwtStatusBadge } from '../../dwt/components/DwtBadges';
import {
  countdownText,
  DWT_STATUS_HELP,
  dwtStatusLabel,
  formatDateTime,
  NO_CONSIGNMENT_REASON_LABEL,
} from '../../dwt/dwtLabels';
import { describeSubmitResult } from '../../dwt/submitResult';
import styles from '../../dwt/dwt.module.css';

export interface ReceiptDwtPanelProps {
  encId: string;
  /** Called after anything that changes DWT state, so the page can refresh related data. */
  onChanged?: () => void;
}

const SUBMITTABLE = new Set<string | null>([null, 'queued', 'failed_retryable', 'failed_permanent', 'blocked_missing_data']);

const NO_CONSIGNMENT_OPTIONS: Array<{ value: NoConsignmentReasonCode; label: string }> = [
  { value: 'NON_HAZ_WASTE_TRANSFER', label: NO_CONSIGNMENT_REASON_LABEL['NON_HAZ_WASTE_TRANSFER']! },
  { value: 'NO_DOC_WITH_WASTE', label: NO_CONSIGNMENT_REASON_LABEL['NO_DOC_WITH_WASTE']! },
  { value: 'HWRC_RECEIPT', label: NO_CONSIGNMENT_REASON_LABEL['HWRC_RECEIPT']! },
];

// -- the DWT details form --------------------------------------------------------------

interface FormValues {
  weight: string;
  estimate: boolean;
  vehicle: string;
  code: string;
  reason: '' | NoConsignmentReasonCode;
}

const EW_CODE = /^[A-Za-z0-9]{6}\/[A-Za-z0-9]{5}$/;

function toForm(state: DwtReceiptState): FormValues {
  const e = state.extras;
  return {
    weight: e.waste_weight_kg === null ? '' : String(e.waste_weight_kg),
    estimate: e.weight_is_estimate,
    vehicle: e.carrier_vehicle_registration ?? '',
    code: e.hazardous_consignment_code ?? '',
    reason: e.reason_for_no_consignment_code ?? '',
  };
}

/** Same rules as the server (receiptExtras.mergeExtras), so mistakes show before saving. */
export function validateForm(f: FormValues, maxWeightKg: number): Partial<Record<keyof FormValues, string>> {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (f.weight.trim() !== '') {
    const w = Number(f.weight);
    if (!Number.isFinite(w) || w <= 0) errors.weight = 'Weight must be greater than 0';
    else if (w > maxWeightKg) errors.weight = `Weight cannot be more than ${maxWeightKg} kg`;
  }
  const reg = f.vehicle.replace(/\s+/g, '');
  if (reg.length > 10) errors.vehicle = 'Registration can be at most 10 characters';
  else if (reg !== '' && !/^[A-Za-z0-9]+$/.test(reg)) errors.vehicle = 'Registration can only contain letters and numbers';
  if (f.code.trim() !== '' && !EW_CODE.test(f.code.trim())) {
    errors.code = 'Use the England and Wales form, for example AB1234/12345';
  }
  if (f.code.trim() !== '' && f.reason !== '') errors.reason = 'Give either a consignment note code or a reason for none, not both';
  return errors;
}

function buildPatch(state: DwtReceiptState, f: FormValues): DwtExtrasPatch {
  const e = state.extras;
  const patch: DwtExtrasPatch = {};
  const w = f.weight.trim() === '' ? null : Number(f.weight);
  if (w !== e.waste_weight_kg) patch.waste_weight_kg = w;
  if (f.estimate !== e.weight_is_estimate) patch.weight_is_estimate = f.estimate;
  const reg = f.vehicle.replace(/\s+/g, '').toUpperCase();
  if ((reg === '' ? null : reg) !== e.carrier_vehicle_registration) patch.carrier_vehicle_registration = reg === '' ? null : reg;
  const code = f.code.trim() === '' ? null : f.code.trim();
  if (code !== e.hazardous_consignment_code) patch.hazardous_consignment_code = code;
  const reason = f.reason === '' ? null : f.reason;
  if (reason !== e.reason_for_no_consignment_code) patch.reason_for_no_consignment_code = reason;
  return patch;
}

function serverFieldErrors(err: unknown): { message: string; fields: Record<string, string> } {
  const data = (err as { response?: { data?: { message?: string; fields?: Record<string, string> } } } | null)?.response?.data;
  return { message: data?.message ?? 'Could not save the Digital Waste Tracking details.', fields: data?.fields ?? {} };
}

const FIELD_TO_FORM: Record<string, keyof FormValues> = {
  waste_weight_kg: 'weight',
  weight_is_estimate: 'estimate',
  carrier_vehicle_registration: 'vehicle',
  hazardous_consignment_code: 'code',
  reason_for_no_consignment_code: 'reason',
};

function ExtrasForm({ state, onSaved }: { state: DwtReceiptState; onSaved: (amendmentRequired: boolean) => void }): JSX.Element {
  const toast = useToast();
  const [form, setForm] = useState<FormValues>(() => toForm(state));
  const [serverErrors, setServerErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const errors = useMemo(() => validateForm(form, state.limits.max_weight_kg), [form, state.limits.max_weight_kg]);
  const patch = useMemo(() => buildPatch(state, form), [state, form]);
  const dirty = Object.keys(patch).length > 0;
  const hasErrors = Object.keys(errors).length > 0;

  // Re-sync when fresh data arrives (after a save or a refetch) and nothing is being edited.
  useEffect(() => {
    setForm(toForm(state));
    setServerErrors({});
  }, [JSON.stringify(state.extras)]);

  const save = useMutation({
    mutationFn: () => saveReceiptExtras(state.receipt_enc_id, patch),
    onSuccess: (res) => {
      if (!res.changed) toast.info('Nothing to save.');
      else if (res.amendment_required) {
        toast.warning('Saved. Defra already has this receipt, so an amendment is needed to send the change.', 8000);
      } else toast.success('Digital Waste Tracking details saved.');
      onSaved(res.amendment_required);
    },
    onError: (err) => {
      const { message, fields } = serverFieldErrors(err);
      const mapped: Partial<Record<keyof FormValues, string>> = {};
      for (const [k, v] of Object.entries(fields)) {
        const key = FIELD_TO_FORM[k];
        if (key) mapped[key] = v;
      }
      setServerErrors(mapped);
      toast.error(Object.keys(mapped).length > 0 ? 'Please correct the highlighted details.' : message);
    },
  });

  const set = <K extends keyof FormValues>(k: K, v: FormValues[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setServerErrors((e) => ({ ...e, [k]: undefined }));
  };
  const err = (k: keyof FormValues) => errors[k] ?? serverErrors[k];

  return (
    <form
      className={styles.stack}
      aria-label="Digital Waste Tracking details"
      onSubmit={(e) => {
        e.preventDefault();
        if (!hasErrors && dirty) save.mutate();
      }}
    >
      <div className={styles.fieldGrid}>
        <div className={styles.field}>
          <label htmlFor="dwt-weight">Weight (kg)</label>
          <input
            id="dwt-weight"
            inputMode="decimal"
            value={form.weight}
            onChange={(e) => set('weight', e.target.value)}
            aria-invalid={err('weight') ? true : undefined}
          />
          {err('weight') ? <span className={styles.fieldError}>{err('weight')}</span> : null}
          <label className={styles.check}>
            <input type="checkbox" checked={form.estimate} onChange={(e) => set('estimate', e.target.checked)} />
            Weight is an estimate
          </label>
          <span className={styles.muted}>
            Leave the weight empty to use the default on the product profile (always sent as an estimate).
          </span>
        </div>

        <div className={styles.field}>
          <label htmlFor="dwt-vehicle">Carrier vehicle registration</label>
          <input
            id="dwt-vehicle"
            value={form.vehicle}
            maxLength={12}
            onChange={(e) => set('vehicle', e.target.value)}
            aria-invalid={err('vehicle') ? true : undefined}
          />
          {err('vehicle') ? <span className={styles.fieldError}>{err('vehicle')}</span> : null}
          <span className={styles.muted}>
            The vehicle that brought the waste. Leave empty to use the driver’s vehicle or the company default.
          </span>
        </div>

        <div className={styles.field}>
          <label htmlFor="dwt-code">Consignment note code</label>
          <input
            id="dwt-code"
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            aria-invalid={err('code') ? true : undefined}
            placeholder="AB1234/12345"
          />
          {err('code') ? <span className={styles.fieldError}>{err('code')}</span> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="dwt-reason">Reason for no consignment note</label>
          <select
            id="dwt-reason"
            value={form.reason}
            onChange={(e) => set('reason', e.target.value as FormValues['reason'])}
            aria-invalid={err('reason') ? true : undefined}
          >
            <option value="">None</option>
            {NO_CONSIGNMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {err('reason') ? <span className={styles.fieldError}>{err('reason')}</span> : null}
          <span className={styles.muted}>
            {state.profile_hazardous === false
              ? 'Not used: this product is not hazardous.'
              : 'Only used for hazardous waste. Leave both empty to use the default on the product profile.'}
          </span>
        </div>
      </div>
      <div className={styles.row}>
        <Button type="submit" icon={Save} loading={save.isPending} disabled={!dirty || hasErrors}>
          Save details
        </Button>
        {dirty ? <span className={styles.muted}>Unsaved changes</span> : null}
      </div>
    </form>
  );
}

// -- summaries and history -------------------------------------------------------------

function consignmentText(s: DwtSummary): string {
  switch (s.consignment.kind) {
    case 'code':
      return `Consignment note ${s.consignment.value ?? ''}`;
    case 'reason':
      return NO_CONSIGNMENT_REASON_LABEL[s.consignment.value ?? ''] ?? 'Reason given';
    case 'not_applicable':
      return 'Not needed (not hazardous)';
    case 'missing':
      return 'Missing';
  }
}

function weightText(s: DwtSummary): string {
  if (s.weight_kg === null) return 'Missing';
  return `${s.weight_kg} kg (${s.weight_is_estimate ? 'estimate' : 'measured'})`;
}

const rows = (s: DwtSummary): Array<[string, string]> => [
  ['EWC code', s.ewc_codes.length > 0 ? s.ewc_codes.join(', ') : '—'],
  ['Waste description', s.waste_description ?? '—'],
  ['Weight', weightText(s)],
  ['Carrier vehicle registration', s.carrier_vehicle_registration ?? 'Missing'],
  ['Consignment information', consignmentText(s)],
  ['Date received', formatDateTime(s.date_received)],
];

function SummaryTable({ title, summary, compareTo }: { title: string; summary: DwtSummary; compareTo?: DwtSummary | null }): JSX.Element {
  const other = compareTo ? rows(compareTo) : null;
  return (
    <div>
      <h4 style={{ margin: '0 0 0.4rem' }}>{title}</h4>
      <dl className={styles.facts}>
        {rows(summary).map(([label, value], i) => {
          const changed = other !== null && other[i]![1] !== value;
          return (
            <div key={label} style={{ display: 'contents' }}>
              <dt className={styles.factLabel}>{label}</dt>
              <dd className={styles.factValue} data-changed={changed ? 'true' : undefined}>
                {value}
                {changed ? <strong> (changed)</strong> : null}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function MessageList({ messages }: { messages: DwtMessage[] }): JSX.Element | null {
  if (messages.length === 0) return null;
  return (
    <ul className={styles.list} aria-label="Messages">
      {messages.map((m, i) => (
        <li key={`${m.severity}-${i}`} data-severity={m.severity}>
          <strong>{m.severity === 'error' ? 'Problem' : m.severity === 'warning' ? 'Warning' : 'Note'}</strong>
          {m.field_label ? ` (${m.field_label})` : ''}: {m.message}
        </li>
      ))}
    </ul>
  );
}

function attemptOutcome(s: DwtSubmissionDto): string {
  if (s.failure_code === 'movement_not_found') return 'Defra could not find the movement';
  return dwtStatusLabel(s.status);
}

// -- the panel -------------------------------------------------------------------------

export function ReceiptDwtPanel({ encId, onChanged }: ReceiptDwtPanelProps): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<null | 'submit' | 'amend'>(null);
  const [busy, setBusy] = useState(false);

  const q = useQuery({
    queryKey: ['dwt', 'receipt', encId],
    queryFn: () => fetchReceiptDwtState(encId),
    retry: false,
    staleTime: 10_000,
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['dwt', 'receipt', encId] });
    void qc.invalidateQueries({ queryKey: ['receipts'] });
    onChanged?.();
  }

  async function run(kind: 'submit' | 'amend') {
    setBusy(true);
    try {
      const r = kind === 'amend' ? await sendReceiptAmendment(encId) : await submitReceiptNow(encId);
      const note = describeSubmitResult(r, kind === 'amend' ? 'amendment' : 'report');
      if (note.tone === 'success') toast.success(note.text, 8000);
      else if (note.tone === 'warning') toast.warning(note.text, 8000);
      else if (note.tone === 'info') toast.info(note.text, 8000);
      else toast.error(note.text, 12000);
    } catch {
      toast.error('Could not contact the server. Please try again.');
    } finally {
      setBusy(false);
      setConfirm(null);
      refresh();
    }
  }

  const notify = useMutation({
    mutationFn: () => recordErrorNotified(encId),
    onSuccess: (r) => {
      toast.success(`Recorded. The correction is due by ${formatDateTime(r.fix_due_by)}.`, 8000);
      refresh();
    },
    onError: () => toast.error('Could not record the notification.'),
  });

  if (q.isLoading) {
    return (
      <Card title="Digital Waste Tracking">
        <div className={styles.row}>
          <Spinner /> <span>Loading…</span>
        </div>
      </Card>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Card title="Digital Waste Tracking">
        <p role="alert">Digital Waste Tracking details could not be loaded.</p>
      </Card>
    );
  }

  const st = q.data;
  const status = st.dwt_status;
  const reported = status === 'submitted' || status === 'amendment_required' || st.summary.sent !== null;
  const current = st.current;
  const dueState: DwtDueState | null = reported ? null : st.due_in;
  const submittable = SUBMITTABLE.has(status);
  const amendRow = st.amendment;

  return (
    <Card title="Digital Waste Tracking">
      <div className={styles.stack} data-testid="dwt-panel">
        <div className={styles.row}>
          <DwtStatusBadge status={status} />
          <DwtDueBadge state={dueState} />
          {st.waste_tracking_id ? (
            <span>
              Waste Tracking ID <strong>{st.waste_tracking_id}</strong>
            </span>
          ) : null}
        </div>
        <p className={styles.muted} style={{ margin: 0 }}>
          {DWT_STATUS_HELP[status ?? 'none']}
        </p>

        {st.due_by && !reported ? (
          <p style={{ margin: 0 }}>
            Due by <strong>{formatDateTime(st.due_by)}</strong> <span className={styles.muted}>({countdownText(st.due_by)})</span>
          </p>
        ) : null}

        {amendRow.required ? (
          <div className={`${styles.banner} ${styles.bannerWarning}`} role="status">
            <span className={styles.bannerTitle}>
              <AlertTriangle size={16} aria-hidden /> Amendment required
            </span>
            <span>
              This receipt was changed after Defra accepted it, so Defra still has the old details. Send an amendment to
              replace them.
            </span>
            {amendRow.due_by ? (
              <span>
                Correct it by <strong>{formatDateTime(amendRow.due_by)}</strong> ({countdownText(amendRow.due_by)}).
                {amendRow.error_notified_at
                  ? ' Defra has told us about an error, so the 2 working day limit applies.'
                  : ' This is within 1 month of the change being made.'}
              </span>
            ) : null}
            <div className={styles.row}>
              <Button icon={FileUp} onClick={() => setConfirm('amend')} loading={busy}>
                Send amendment
              </Button>
              {!amendRow.error_notified_at ? (
                <Button variant="ghost" size="sm" onClick={() => notify.mutate()} loading={notify.isPending}>
                  Defra has told us about an error
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {amendRow.movement_not_found ? (
          <div className={`${styles.banner} ${styles.bannerDanger}`} role="alert">
            <span className={styles.bannerTitle}>Defra could not find this movement</span>
            <span>
              Defra says the Waste Tracking ID {st.waste_tracking_id ?? ''} does not exist. Nothing has been re-sent and nothing
              will be re-created automatically, so a duplicate is not created. Look the ID up in Defra’s Report receipt of
              waste service. If the movement is there, contact support. If it is not, contact support before reporting this
              receipt again.
            </span>
          </div>
        ) : null}

        {!st.eligibility.eligible && !reported && st.eligibility.staff_reasons.length > 0 ? (
          <div className={`${styles.banner} ${styles.bannerInfo}`}>
            <span className={styles.bannerTitle}>Before this can be reported</span>
            {st.eligibility.staff_reasons.map((r) => (
              <div key={r.code} className={styles.reason}>
                <span>{r.message}</span>
                <span className={styles.fix}>{r.fix}</span>
              </div>
            ))}
          </div>
        ) : null}

        {current && current.messages.length > 0 ? (
          <div>
            <h4 style={{ margin: '0 0 0.4rem' }}>Messages from the latest attempt</h4>
            <MessageList messages={current.messages} />
          </div>
        ) : null}

        {submittable && st.eligibility.eligible ? (
          <div className={styles.row}>
            <Button variant="secondary" icon={FileUp} onClick={() => setConfirm('submit')} loading={busy}>
              Submit to Defra now
            </Button>
          </div>
        ) : null}

        <div>
          {st.summary.sent && amendRow.open && st.summary.will_send ? (
            <div className={styles.fieldGrid}>
              <SummaryTable title="Defra currently holds" summary={st.summary.sent} />
              <SummaryTable title="Will be sent in the amendment" summary={st.summary.will_send} compareTo={st.summary.sent} />
            </div>
          ) : st.summary.sent ? (
            <SummaryTable title="Sent to Defra" summary={st.summary.sent} />
          ) : st.summary.will_send ? (
            <SummaryTable title="Will be sent to Defra" summary={st.summary.will_send} />
          ) : (
            <p className={styles.muted}>
              There is no Defra waste profile for this receipt’s product yet, so there is nothing to show.
            </p>
          )}
        </div>

        <div>
          <h4 style={{ margin: '0 0 0.4rem' }}>Digital Waste Tracking details</h4>
          <ExtrasForm
            state={st}
            onSaved={() => {
              refresh();
            }}
          />
        </div>

        <div>
          <h4 style={{ margin: '0 0 0.4rem' }}>Attempts</h4>
          {st.history.length === 0 ? (
            <p className={styles.muted}>Nothing has been sent to Defra for this receipt yet.</p>
          ) : (
            <table className={styles.table} aria-label="Attempt history">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Outcome</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {st.history.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDateTime(h.updated_at ?? h.created_at)}</td>
                    <td>{h.kind === 'amend' ? 'Amendment' : 'Report'}</td>
                    <td>{attemptOutcome(h)}</td>
                    <td>
                      {h.messages.length === 0 ? (
                        <span className={styles.muted}>—</span>
                      ) : (
                        <MessageList messages={h.messages} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirm !== null}
        title={confirm === 'amend' ? 'Send amendment to Defra?' : 'Submit to Defra now?'}
        message={
          confirm === 'amend'
            ? 'Defra’s copy of this receipt will be replaced with the receipt as it is now.'
            : 'This reports the receipt to Defra straight away instead of waiting for the queue.'
        }
        confirmLabel={confirm === 'amend' ? 'Send amendment' : 'Submit now'}
        cancelLabel="Cancel"
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => (confirm === null ? undefined : run(confirm))}
      />
    </Card>
  );
}
